import { BOOLEAN_BYTES_PER_ELEMENT, BusMessage, MessageFactory, UID_BYTES_PER_ELEMENT, UID_SAFE_BYTES_PER_ELEMENT, ENUM_BYTES_PER_ELEMENT, NUMBER3_BYTES_PER_ELEMENT, NUMBER4_BYTES_PER_ELEMENT, NUMBER16_BYTES_PER_ELEMENT, calcArrayByteLength, ButtonType } from "flavr-common";
/**
 * Requests whether a model can be picked up.
 */
export class RequestCanPickupModelMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // uid
            UID_SAFE_BYTES_PER_ELEMENT); // controllerUid
    }
    constructor(uid, controllerUid, offset) {
        super();
        this.uid = uid;
        this.controllerUid = controllerUid;
        this.offset = offset;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.controllerUid = stream.uidSafe(this.controllerUid);
    }
    static is(msg) {
        return msg.type === RequestCanPickupModelMessage.typestatic;
    }
}
RequestCanPickupModelMessage.typestatic = "RequestCanPickupModelMessage";
RequestCanPickupModelMessage.subjectstatic = "general";
MessageFactory.register(RequestCanPickupModelMessage);
/**
 * Request to pick a model up. Returns a PickupModelResponseMessage
 */
export class PickupModelMessage extends BusMessage {
    get byteLength() {
        let byteLength = UID_BYTES_PER_ELEMENT + // uid
            UID_SAFE_BYTES_PER_ELEMENT + // controllerUid
            BOOLEAN_BYTES_PER_ELEMENT + // keepSelectionActiveInVR
            BOOLEAN_BYTES_PER_ELEMENT + // allowQuickReplacement
            BOOLEAN_BYTES_PER_ELEMENT + // hasOffset
            ENUM_BYTES_PER_ELEMENT + // dropOffMode
            ENUM_BYTES_PER_ELEMENT + // dropOffButton
            BOOLEAN_BYTES_PER_ELEMENT + // createTemporaryModel
            BOOLEAN_BYTES_PER_ELEMENT + // deleteOnDropOff
            BOOLEAN_BYTES_PER_ELEMENT + // dropOnReleaseButton
            BOOLEAN_BYTES_PER_ELEMENT; // showDropOffIcon
        if (this.params.offset !== undefined) {
            byteLength +=
                NUMBER3_BYTES_PER_ELEMENT + // position
                    NUMBER4_BYTES_PER_ELEMENT + // orientation
                    NUMBER3_BYTES_PER_ELEMENT; // scale
        }
        byteLength += calcArrayByteLength(this.params.dropOffLayers);
        byteLength += NUMBER16_BYTES_PER_ELEMENT; // originalTransformWorld
        return byteLength;
    }
    constructor(uid, params = {}) {
        super();
        this.uid = uid;
        this.params = params;
    }
    serialization(stream) {
        var _a, _b, _c;
        this.uid = stream.uid(this.uid);
        if (stream.isReading) {
            this.params = {};
        }
        this.params.controllerUid = stream.uidSafe(this.params.controllerUid);
        this.params.keepSelectionActiveInVR = stream.boolean((_a = this.params.keepSelectionActiveInVR) !== null && _a !== void 0 ? _a : false);
        const hasOffset = stream.boolean(this.params.offset !== undefined);
        if (hasOffset) {
            this.params.offset = (_b = this.params.offset) !== null && _b !== void 0 ? _b : { position: [0, 0, 0], orientation: [0, 0, 0, 0], scale: [0, 0, 0] };
            this.params.offset.position = stream.number3(this.params.offset.position);
            this.params.offset.orientation = stream.number4(this.params.offset.orientation);
            this.params.offset.scale = stream.number3(this.params.offset.scale);
        }
        else {
            this.params.offset = undefined;
        }
        this.params.allowQuickReplacement = stream.booleanSafe(this.params.allowQuickReplacement);
        this.params.dropOffMode = stream.enumSafe(this.params.dropOffMode);
        this.params.dropOffLayers = stream.arraySafe(this.params.dropOffLayers);
        this.params.dropOffButton = (_c = stream.enumSafe(this.params.dropOffButton)) !== null && _c !== void 0 ? _c : ButtonType.DISCARD;
        this.params.createTemporaryModel = stream.booleanSafe(this.params.createTemporaryModel);
        this.params.deleteOnDropOff = stream.booleanSafe(this.params.deleteOnDropOff);
        this.params.dropOnReleaseButton = stream.booleanSafe(this.params.dropOnReleaseButton);
        this.params.showDropOffIcon = stream.booleanSafe(this.params.showDropOffIcon);
        if (this.params.createTemporaryModel === undefined) {
            this.params.createTemporaryModel = false;
        }
        if (this.params.deleteOnDropOff === undefined) {
            this.params.deleteOnDropOff = this.params.createTemporaryModel;
        }
        this.params.originalTransformWorld = stream.number16Safe(this.params.originalTransformWorld);
    }
    static is(msg) {
        return msg.type === PickupModelMessage.typestatic;
    }
}
PickupModelMessage.typestatic = "PickupModelMessage";
PickupModelMessage.subjectstatic = "general";
MessageFactory.register(PickupModelMessage);
/**
 * Response to PickupModelMessage. Returns the uid of the picked up model. This uid can be different to the requested
 * model uid, if createTemporaryModel in the PickupModelMessage was set to true
 */
export class PickupModelResponseMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    constructor(modelUID) {
        super();
        this.modelUID = modelUID;
    }
    serialization(stream) {
        this.modelUID = stream.uid(this.modelUID);
    }
    static is(msg) {
        return msg.type === PickupModelResponseMessage.typestatic;
    }
}
PickupModelResponseMessage.typestatic = "PickupModelResponseMessage";
PickupModelResponseMessage.subjectstatic = "general";
MessageFactory.register(PickupModelResponseMessage);
/**
 * Requests dropping a currently picked up model.
 */
export class DropModelMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT; // uid
    }
    constructor(uid) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === DropModelMessage.typestatic;
    }
}
DropModelMessage.typestatic = "DropModelMessage";
DropModelMessage.subjectstatic = "general";
MessageFactory.register(DropModelMessage);
/**
 * Releases pickups before restoring scenario transforms, even outside valid drop-off areas.
 * Answered with `AskOkMessage` after pickup bookkeeping and attachment messages are updated.
 */
export class ResetModelPickupsRequestMessage extends BusMessage {
    constructor(modelUids = []) {
        super();
        this.modelUids = modelUids;
    }
    get byteLength() {
        return Uint32Array.BYTES_PER_ELEMENT + UID_BYTES_PER_ELEMENT * this.modelUids.length;
    }
    serialization(stream) {
        if (stream.isReading)
            this.modelUids = [];
        const length = stream.uint32(this.modelUids.length);
        for (let i = 0; i < length; i++)
            this.modelUids[i] = stream.uid(this.modelUids[i]);
    }
    static is(msg) {
        return msg.type === ResetModelPickupsRequestMessage.typestatic;
    }
}
ResetModelPickupsRequestMessage.typestatic = "ResetModelPickupsRequestMessage";
ResetModelPickupsRequestMessage.subjectstatic = "general";
MessageFactory.register(ResetModelPickupsRequestMessage);
/**
 * Requests dropping the model held by a controller.
 */
export class DropFromControllerMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // uid
            BOOLEAN_BYTES_PER_ELEMENT + // destroyModels
            BOOLEAN_BYTES_PER_ELEMENT + // showSelection
            BOOLEAN_BYTES_PER_ELEMENT + // enableSelection
            BOOLEAN_BYTES_PER_ELEMENT); // showMarkerRader
    }
    constructor(uid, destroyModels = false) {
        super();
        this.uid = uid;
        this.destroyModels = destroyModels;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.destroyModels = stream.boolean(this.destroyModels);
    }
    static is(msg) {
        return msg.type === DropFromControllerMessage.typestatic;
    }
}
DropFromControllerMessage.typestatic = "DropFromControllerMessage";
DropFromControllerMessage.subjectstatic = "general";
MessageFactory.register(DropFromControllerMessage);
/**
 * Announces that a model was picked up.
 */
export class OnPickupModelMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + UID_SAFE_BYTES_PER_ELEMENT + UID_SAFE_BYTES_PER_ELEMENT;
    }
    constructor(uid, controllerUid, originalModelUid) {
        super();
        this.uid = uid;
        this.controllerUid = controllerUid;
        this.originalModelUid = originalModelUid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.controllerUid = stream.uidSafe(this.controllerUid);
        this.originalModelUid = stream.uidSafe(this.originalModelUid);
    }
    static is(msg) {
        return msg.type === OnPickupModelMessage.typestatic;
    }
}
OnPickupModelMessage.typestatic = "OnPickupModelMessage";
OnPickupModelMessage.subjectstatic = "general";
MessageFactory.register(OnPickupModelMessage);
/**
 * Announces that a model was dropped.
 */
export class OnDropModelMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + UID_SAFE_BYTES_PER_ELEMENT; // modelUid, controllerUid
    }
    constructor(modelUid, controllerUid) {
        super();
        this.modelUid = modelUid;
        this.controllerUid = controllerUid;
    }
    serialization(stream) {
        this.modelUid = stream.uid(this.modelUid);
        this.controllerUid = stream.uidSafe(this.controllerUid);
    }
    static is(msg) {
        return msg.type === OnDropModelMessage.typestatic;
    }
}
OnDropModelMessage.typestatic = "OnDropModelMessage";
OnDropModelMessage.subjectstatic = "general";
MessageFactory.register(OnDropModelMessage);
/// Attach to local player messages ///
/**
 * Requests attaching a model to another model.
 */
export class AttachToModelMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // childUID
            UID_BYTES_PER_ELEMENT + // parentUID
            NUMBER16_BYTES_PER_ELEMENT // localTransform
        );
    }
    constructor(childUID, parentUID, localTransform) {
        super();
        this.childUID = childUID;
        this.parentUID = parentUID;
        this.localTransform = localTransform;
    }
    serialization(stream) {
        this.childUID = stream.uid(this.childUID);
        this.parentUID = stream.uid(this.parentUID);
        this.localTransform = stream.number16Safe(this.localTransform);
    }
    static is(msg) {
        return msg.type === AttachToModelMessage.typestatic;
    }
}
AttachToModelMessage.typestatic = "AttachToModelMessage";
AttachToModelMessage.subjectstatic = "general";
MessageFactory.register(AttachToModelMessage);
/// Attach to local player messages ///
/**
 * Requests whether the local player is attached to a model.
 */
export class RequestIsAttachedLocalPlayerMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    constructor(uid) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === RequestIsAttachedLocalPlayerMessage.typestatic;
    }
}
RequestIsAttachedLocalPlayerMessage.typestatic = "RequestIsAttachedLocalPlayerMessage";
RequestIsAttachedLocalPlayerMessage.subjectstatic = "general";
MessageFactory.register(RequestIsAttachedLocalPlayerMessage);
/**
 * Returns whether the local player is attached to a model.
 */
export class ResponseIsAttachedLocalPlayerMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + BOOLEAN_BYTES_PER_ELEMENT;
    }
    constructor(uid, isAttached) {
        super();
        this.uid = uid;
        this.isAttached = isAttached;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.isAttached = stream.boolean(this.isAttached);
    }
    static is(msg) {
        return msg.type === ResponseIsAttachedLocalPlayerMessage.typestatic;
    }
}
ResponseIsAttachedLocalPlayerMessage.typestatic = "ResponseIsAttachedLocalPlayerMessage";
ResponseIsAttachedLocalPlayerMessage.subjectstatic = "general";
MessageFactory.register(ResponseIsAttachedLocalPlayerMessage);
/**
 * This message is sent when a child object is either attached, detached or the attachment changed directly. If the
 * child is attached then the `previousParentUID` is undefined, if it is detached then the `newParentUID` is undefined.
 * If both parent fields are defined then the attachment parent simply changed. It shouldn't happen that both parents
 * are undefined.
 */
export class AttachmentChangedMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // childUID
            UID_SAFE_BYTES_PER_ELEMENT * 2 // previousParentUID, newParentUID
        );
    }
    constructor(childUID, previousParentUID, newParentUID) {
        super();
        this.childUID = childUID;
        this.previousParentUID = previousParentUID;
        this.newParentUID = newParentUID;
    }
    serialization(stream) {
        this.childUID = stream.uid(this.childUID);
        this.previousParentUID = stream.uidSafe(this.previousParentUID);
        this.newParentUID = stream.uidSafe(this.newParentUID);
    }
    static is(msg) {
        return msg.type === AttachmentChangedMessage.typestatic;
    }
}
AttachmentChangedMessage.typestatic = "AttachmentChangedMessage";
AttachmentChangedMessage.subjectstatic = "general";
MessageFactory.register(AttachmentChangedMessage);
